<template>
  <div class="admin-notifications-container">
    <!-- Filtros -->
    <div class="notif-filters card mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12 col-md-3">
            <label class="form-label small text-muted">Categoria</label>
            <select
              class="form-select form-select-sm"
              v-model="localFilters.category"
              @change="applyFilters"
            >
              <option :value="undefined">Todas</option>
              <option
                v-for="c in categories"
                :key="c.value"
                :value="c.value"
              >
                {{ c.label }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small text-muted">Status do envio</label>
            <select
              class="form-select form-select-sm"
              v-model="localFilters.status"
              @change="applyFilters"
            >
              <option :value="undefined">Todos</option>
              <option v-for="s in statuses" :key="s" :value="s">
                {{ s }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-2">
            <label class="form-label small text-muted">Tipo</label>
            <select
              class="form-select form-select-sm"
              v-model="localFilters.type"
              @change="applyFilters"
            >
              <option :value="undefined">Todos</option>
              <option v-for="t in types" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="col-12 col-md-2">
            <label class="form-label small text-muted">De</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="localFilters.from"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-2">
            <label class="form-label small text-muted">Até</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="localFilters.to"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-4 d-flex align-items-center gap-2 mt-4">
            <input
              type="checkbox"
              class="form-check-input"
              id="somenteFalhas"
              v-model="localFilters.somente_falhas"
              @change="applyFilters"
            />
            <label class="form-check-label small" for="somenteFalhas">
              <i class="fa-solid fa-triangle-exclamation text-warning me-1"></i>
              Só falhas (não chegou ao aparelho)
            </label>
          </div>
          <div class="col-12 col-md-8 d-flex align-items-end justify-content-end">
            <button class="btn btn-sm btn-outline-secondary" @click="clearFilters">
              <i class="fa-solid fa-eraser me-1"></i>Limpar filtros
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!items.length" class="text-center py-5 text-muted">
      <i class="fa-solid fa-bell-slash fa-3x mb-3 opacity-25"></i>
      <p class="mb-0">Nenhuma notificação encontrada</p>
    </div>

    <!-- Feed -->
    <div v-else>
      <div class="notif-list">
        <div v-for="n in items" :key="n.id" class="notif-item">
          <div class="notif-item-main">
            <div class="notif-item-badges">
              <span class="badge bg-light text-dark border">
                <i :class="n.type === 'email' ? 'fa-solid fa-envelope' : 'fa-solid fa-bell'"></i>
                {{ n.type }}
              </span>
              <span class="badge" :class="statusInfo(n).class">
                {{ statusInfo(n).label }}
              </span>
              <span v-if="n.category_label" class="badge bg-secondary">
                {{ n.category_label }}
              </span>
              <span v-if="!n.read" class="badge bg-primary-soft">Não lida</span>
            </div>
            <div class="notif-item-title">{{ n.title }}</div>
            <div class="notif-item-desc">{{ n.description }}</div>
          </div>
          <div class="notif-item-meta text-muted small">
            <span><i class="fa-solid fa-clock me-1"></i>{{ formatDate(n.created_at) }}</span>
            <router-link
              v-if="n.target_type === 'appointment' && n.target_id"
              :to="`${$paths.appointments.detail}/${n.target_id}`"
              target="_blank"
            >
              <i class="fa-solid fa-bullseye me-1"></i>{{ n.target_type }} #{{ n.target_id }}
            </router-link>
            <span v-else-if="n.target_type">
              <i class="fa-solid fa-bullseye me-1"></i>{{ n.target_type }}
              <span v-if="n.target_id">#{{ n.target_id }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Paginação -->
      <div class="d-flex justify-content-between align-items-center mt-3" v-if="lastPage > 1">
        <span class="text-muted small">
          Mostrando {{ items.length }} de {{ total }} registros
        </span>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage <= 1 || loading"
            @click="changePage(currentPage - 1)"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="align-self-center small">
            Página {{ currentPage }} de {{ lastPage }}
          </span>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage >= lastPage || loading"
            @click="changePage(currentPage + 1)"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdminNotificationsList",
  props: {
    baseFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      categories: [],
      statuses: [],
      types: [],
      localFilters: {
        category: undefined,
        status: undefined,
        type: undefined,
        from: undefined,
        to: undefined,
        somente_falhas: false,
      },
    };
  },
  mounted() {
    this.loadFilters();
    this.fetchNotifications();
  },
  watch: {
    baseFilters: {
      handler() {
        this.currentPage = 1;
        this.fetchNotifications();
      },
      deep: true,
    },
  },
  methods: {
    async loadFilters() {
      try {
        const res = await this.$notificationAdminService.listFilters();
        this.categories = res.data?.categories || [];
        this.statuses = res.data?.statuses || [];
        this.types = res.data?.types || [];
      } catch (e) {
        console.error("Erro ao carregar filtros de notificações:", e);
      }
    },
    async fetchNotifications() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, ...this.baseFilters };
        Object.keys(this.localFilters).forEach((k) => {
          const v = this.localFilters[k];
          if (v !== undefined && v !== "" && v !== null && v !== false) {
            params[k] = v;
          }
        });
        const res = await this.$notificationAdminService.list(params);
        const page = res.data || {};
        this.items = page.data || [];
        this.currentPage = page.current_page || 1;
        this.lastPage = page.last_page || 1;
        this.total = page.total || 0;
      } catch (e) {
        console.error("Erro ao carregar notificações:", e);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar notificações.",
        });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchNotifications();
    },
    clearFilters() {
      Object.keys(this.localFilters).forEach((k) => {
        this.localFilters[k] = k === "somente_falhas" ? false : undefined;
      });
      this.currentPage = 1;
      this.fetchNotifications();
    },
    changePage(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      this.fetchNotifications();
    },
    // Uma notificação falha de 3 jeitos (doc admin-notifications.md): sem token,
    // erro no envio, ou aceita pelo Expo mas recusada pelo aparelho (o caso
    // traiçoeiro: status=success + receipt_status=error).
    statusInfo(n) {
      if (n.status === "no_token") return { label: "Sem token", class: "bg-secondary" };
      if (n.status === "error") return { label: "Erro no envio", class: "bg-danger" };
      if (n.status === "canceled") return { label: "Cancelado", class: "bg-secondary" };
      if (n.status === "pending") return { label: "Pendente", class: "bg-warning text-dark" };
      // status === "success"
      if (n.receipt_status === "error") {
        return { label: "Recusado pelo aparelho", class: "bg-danger" };
      }
      if (n.receipt_status === "pending") {
        return { label: "Aguardando confirmação", class: "bg-warning text-dark" };
      }
      return { label: "Entregue", class: "bg-success" };
    },
    formatDate(date) {
      return this.$moment(date).format("DD/MM/YYYY HH:mm");
    },
  },
};
</script>

<style scoped>
.notif-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notif-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #fff;
  padding: 14px 18px;
}

.notif-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.notif-item-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.notif-item-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #212529;
}

.notif-item-desc {
  font-size: 0.87rem;
  color: #495057;
  line-height: 1.4;
}

.notif-item-meta {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;
  white-space: nowrap;
  padding-top: 2px;
}
</style>
